/**
 * The Play tab — the critical path, laid out as `prototype/index.html` has it.
 *
 * Tap 1: the winning side's Won button.
 * Tap 2: the losing score (ScoreGrid).
 *
 * Three cards, in the order somebody standing on a court reads them:
 *
 *   Next game   who is on, and the two buttons that log the result
 *   Last game   what just happened, still correctable
 *   Rotation    whose turn is coming, and how to change it
 *
 * The first build had the matchup and then a flat queue list, which lost two
 * things the mockup had: the last result (so a wrong score meant hunting for
 * it) and any way to reorder the rotation at all — the endpoint existed and
 * nothing called it.
 *
 * Names sit beside each other rather than stacked, and Won is a compact button
 * next to them rather than a tall slab. They remain visually and physically
 * distinct controls, which is what AC-27g asks for; being distinct never
 * required being enormous.
 */

import { api, type Game, type QueueEntry, type SessionView } from "../api.js";
import { nextPairing, pairingsOf } from "../pairing.js";
import { CourtCards } from "./CourtCards.js";

export interface PlayProps {
  view: SessionView;
  nameOf: (id: string) => string;
  sides: { sideA: string[]; sideB: string[] } | null;
  waiting: QueueEntry[];
  gameType: "doubles" | "singles";
  target: number;
  onTarget: (target: number) => void;
  court: number | null;
  awaitingCourt: boolean;
  override: { sideA: string[]; sideB: string[] } | null;
  swapSlot: string | null;
  error: string | null;
  onSwapSlot: (id: string | null) => void;
  onOverride: (sides: { sideA: string[]; sideB: string[] } | null) => void;
  onGameType: (t: "doubles" | "singles") => void;
  onCourt: (n: number | null) => void;
  onWon: (side: "A" | "B") => void;
  onShowRoster: () => void;
  onChanged: () => Promise<void>;
}

export function PlayScreen(props: PlayProps): React.JSX.Element {
  const { view, nameOf, sides, error } = props;
  const { session, queue, entry, games } = view;

  return (
    <>
      <div className="eyebrow">On court now</div>

      {props.awaitingCourt ? (
        <CourtCards
          courts={entry.courts}
          nameOf={nameOf}
          onPick={(picked) => {
            props.onCourt(picked.courtNumber);
            // AC-34 - tapping a court pre-fills entry with its previous four.
            if (picked.lastSideA.length > 0) {
              props.onOverride({ sideA: picked.lastSideA, sideB: picked.lastSideB });
            }
          }}
        />
      ) : sides ? (
        <NextGame {...props} sides={sides} />
      ) : (
        <section className="matchup empty">
          <p>
            {entry.matchup.available
              ? ""
              : props.gameType === "singles"
                ? "A singles game needs two players here."
                : entry.matchup.message}
          </p>
          <button className="primary" onClick={props.onShowRoster}>
            Who&apos;s here?
          </button>
        </section>
      )}

      {error ? <p className="error">{error}</p> : null}

      <LastGame games={games} nameOf={nameOf} onChanged={props.onChanged} />

      <Rotation
        sessionId={session.id}
        queue={queue}
        onCourt={sides ? [...sides.sideA, ...sides.sideB] : []}
        onShowRoster={props.onShowRoster}
        onChanged={props.onChanged}
      />
    </>
  );
}

/** The card the whole product is measured on. */
function NextGame(
  props: PlayProps & { sides: { sideA: string[]; sideB: string[] } },
): React.JSX.Element {
  const { sides, view, nameOf, waiting } = props;
  const { entry } = view;
  const doubles = props.gameType === "doubles" && sides.sideA.length === 2;
  const targets = [...new Set([entry.targetScore, ...entry.alternateTargets])];

  /**
   * FR-ENTRY-4 — the same four can be arranged three ways, and saying which of
   * the three you are looking at is what makes tapping Swap feel like a cycle
   * rather than a shuffle. Computed here from the canonical sort, so it agrees
   * with what the button will actually do next.
   */
  const cycle = doubles ? pairingsOf([...sides.sideA, ...sides.sideB]) : [];
  const key = (p: { sideA: string[]; sideB: string[] }): string =>
    [[...p.sideA].sort().join("|"), [...p.sideB].sort().join("|")].sort().join("//");
  const pairingIndex = cycle.findIndex((p) => key(p) === key(sides));

  return (
    <section className="matchup">
      <h3 className="card-head">
        Next game
        {doubles ? (
          <button className="mini" onClick={() => props.onOverride(nextPairing(sides.sideA, sides.sideB))}>
            ⇄ Swap partners
          </button>
        ) : null}
      </h3>

      <SideRow
        label="A"
        playerIds={sides.sideA}
        nameOf={nameOf}
        swapSlot={props.swapSlot}
        bench={waiting}
        onTapName={props.onSwapSlot}
        onReplace={(slotId, withId) => {
          props.onOverride({
            sideA: sides.sideA.map((id) => (id === slotId ? withId : id)),
            sideB: sides.sideB,
          });
          props.onSwapSlot(null);
        }}
        onTakeOut={async (playerId) => {
          await api.takeOut(view.session.id, playerId);
          props.onSwapSlot(null);
          props.onOverride(null);
          await props.onChanged();
        }}
        onWon={() => props.onWon("A")}
      />

      <div className="versus">vs</div>

      <SideRow
        label="B"
        playerIds={sides.sideB}
        nameOf={nameOf}
        swapSlot={props.swapSlot}
        bench={waiting}
        onTapName={props.onSwapSlot}
        onReplace={(slotId, withId) => {
          props.onOverride({
            sideA: sides.sideA,
            sideB: sides.sideB.map((id) => (id === slotId ? withId : id)),
          });
          props.onSwapSlot(null);
        }}
        onTakeOut={async (playerId) => {
          await api.takeOut(view.session.id, playerId);
          props.onSwapSlot(null);
          props.onOverride(null);
          await props.onChanged();
        }}
        onWon={() => props.onWon("B")}
      />

      <p className="resting">
        {waiting.length > 0 ? (
          <>
            Waiting: <b>{waiting.map((q) => q.displayName).join(", ")}</b>
          </>
        ) : (
          "Nobody waiting — the same four each game."
        )}
        {pairingIndex >= 0 ? ` · pairing ${String(pairingIndex + 1)} of 3` : null}
      </p>

      <div className="row target-row">
        {entry.courtCount > 1 ? (
          <button
            className="chip chip-on"
            onClick={() => {
              props.onCourt(null);
              props.onOverride(null);
            }}
          >
            Court {props.court} ✕
          </button>
        ) : null}

        {/* FR-ENTRY-6 - doubles pre-selected, singles one tap away. */}
        {(["doubles", "singles"] as const).map((t) => (
          <button
            key={t}
            className={t === props.gameType ? "chip chip-on" : "chip"}
            onClick={() => {
              props.onGameType(t);
              props.onOverride(null);
            }}
          >
            {t}
          </button>
        ))}

        {/*
          FR-ENTRY-5 — the target, set before the game rather than after it.

          This sat on the score screen, on the argument that you find out a game
          was to 21 when somebody says the score. The club disagrees, and they
          are the ones standing on the court: the four players know what they
          are playing to before they start, and being asked again while holding
          a result is a question about something already decided. Setting it
          here also means the grid is already correct when it opens, so a
          21-point game costs the same two taps as any other.

          The session's own target comes first and is preselected, so the common
          case is no tap at all.
        */}
        <span className="muted small">to</span>
        {targets.map((t) => (
          <button
            key={t}
            className={t === props.target ? "chip chip-on" : "chip"}
            onClick={() => props.onTarget(t)}
          >
            {t}
          </button>
        ))}

        {props.override ? (
          <button className="link" onClick={() => props.onOverride(null)}>
            Reset four
          </button>
        ) : null}
      </div>
    </section>
  );
}

/**
 * FR-ENTRY-9 — the most recent result, on the screen you are already on.
 *
 * A wrong score is noticed within a game or two, and the mockup put the last
 * result where it could be corrected without going looking. The first build
 * dropped this card and left Undo at the bottom of a scrolling list.
 */
function LastGame({
  games,
  nameOf,
  onChanged,
}: {
  games: Game[];
  nameOf: (id: string) => string;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const last = games[games.length - 1];

  if (!last) {
    return (
      <section className="block">
        <h3 className="card-head">Last game</h3>
        <p>Nothing logged yet.</p>
      </section>
    );
  }

  const aWon = last.winnerSide === "A";
  return (
    <section className="block lastgame">
      <h3 className="card-head">Last game · to {last.targetScore}</h3>
      <div className={aWon ? "gline win" : "gline"}>
        <span className="nm">{last.sideA.map(nameOf).join(" & ")}</span>
        <span className="sc num">{last.sideAScore}</span>
      </div>
      <div className={aWon ? "gline" : "gline win"}>
        <span className="nm">{last.sideB.map(nameOf).join(" & ")}</span>
        <span className="sc num">{last.sideBScore}</span>
      </div>
      <div className="card-foot">
        <button
          className="chipbtn"
          onClick={() => {
            void api.undoGame(last.id).then(onChanged);
          }}
        >
          Undo this game
        </button>
      </div>
    </section>
  );
}

/**
 * FR-ENTRY-15 — the rotation, and the two ways to change it.
 *
 * The reorder endpoint has existed since 1.4 and nothing has ever called it:
 * the queue was a read-only list. An arrow moves somebody to the front, which
 * is the request that actually gets made at a court ("he has not played for
 * three games"), and Shuffle re-draws the order when the club wants to start
 * again rather than argue about it.
 *
 * Both send the whole order. The server checks the set against the session's
 * attendees, so a stale client cannot drop somebody by reordering.
 */
function Rotation({
  sessionId,
  queue,
  onCourt,
  onShowRoster,
  onChanged,
}: {
  sessionId: string;
  queue: QueueEntry[];
  onCourt: string[];
  onShowRoster: () => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const ids = queue.map((q) => q.playerId);

  function reorder(order: string[], reason: string): void {
    void api.reorderQueue(sessionId, order, reason).then(onChanged);
  }

  return (
    <section className="block rot">
      <h3 className="card-head">
        Rotation
        <button
          className="mini"
          onClick={() => {
            // Fisher-Yates. A sort with a random comparator is not a shuffle —
            // it is biased and, with some engines, not even a permutation.
            const shuffled = [...ids];
            for (let i = shuffled.length - 1; i > 0; i -= 1) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            reorder(shuffled, "Rotation shuffled");
          }}
        >
          Shuffle
        </button>
      </h3>

      {queue.map((q, index) => {
        const playing = onCourt.includes(q.playerId);
        return (
          <div key={q.playerId} className={q.isOutOfRotation ? "rotrow out" : "rotrow"}>
            <span className={playing ? "who now" : "who"}>
              {index + 1}. {q.displayName}
              {playing ? " · on" : null}
              {q.isOutOfRotation ? " · out" : null}
            </span>
            <span className="muted small">
              {q.gamesPlayed} played · {q.gamesSat} sat
            </span>
            {q.isOutOfRotation ? (
              <button
                className="mini"
                onClick={() => {
                  void api.bringBack(sessionId, q.playerId).then(onChanged);
                }}
              >
                Back in
              </button>
            ) : index === 0 ? null : (
              <button
                className="mini arrow"
                aria-label={`Move ${q.displayName} to the front of the rotation`}
                onClick={() =>
                  reorder(
                    [q.playerId, ...ids.filter((id) => id !== q.playerId)],
                    `${q.displayName} moved to the front`,
                  )
                }
              >
                ▲
              </button>
            )}
          </div>
        );
      })}

      <p className="hint">
        Arrow moves a player to the front.{" "}
        <button className="linkish" onClick={onShowRoster}>
          Who&apos;s here?
        </button>{" "}
        to add or remove someone.
      </p>
    </section>
  );
}

/**
 * One side: the names beside each other, and Won as its own button.
 *
 * The swap strip opens below the whole row rather than under one name, so the
 * row does not jump sideways when it appears — the same reason the mockup
 * renders it after the side rather than inside it.
 */
function SideRow({
  label,
  playerIds,
  nameOf,
  swapSlot,
  bench,
  onTapName,
  onReplace,
  onTakeOut,
  onWon,
}: {
  label: string;
  playerIds: string[];
  nameOf: (id: string) => string;
  swapSlot: string | null;
  bench: Array<{ playerId: string; displayName: string }>;
  onTapName: (id: string | null) => void;
  onReplace: (slotId: string, withId: string) => void;
  onTakeOut: (playerId: string) => Promise<void>;
  onWon: () => void;
}): React.JSX.Element {
  const open = playerIds.find((id) => id === swapSlot) ?? null;

  return (
    <>
      <div className="side">
        <div className="side-names">
          {playerIds.map((id) => (
            <button
              key={id}
              className={swapSlot === id ? "name name-open" : "name"}
              onClick={() => onTapName(swapSlot === id ? null : id)}
            >
              {nameOf(id)}
            </button>
          ))}
        </div>

        <button className="won" onClick={onWon} aria-label={`Side ${label} won`}>
          Won
        </button>
      </div>

      {open ? (
        <div className="swap-strip">
          <div className="strip-label">
            Swap <b>{nameOf(open)}</b> for
          </div>
          <div className="strip-options">
            {bench.length > 0 ? (
              bench.map((b) => (
                <button key={b.playerId} className="chip" onClick={() => onReplace(open, b.playerId)}>
                  {b.displayName}
                </button>
              ))
            ) : (
              <span className="muted small">Nobody else is waiting.</span>
            )}
            <button
              className="chip chip-warn"
              onClick={() => {
                void onTakeOut(open);
              }}
            >
              Take {nameOf(open)} out of the rotation
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
